import { textureLoaderService } from "../texture/TextureLoaderService";
import { GridHelper, Mesh, MeshBasicMaterial, BackSide, SphereGeometry, Vector3, Color, PMREMGenerator } from "three";
import { mainScene } from "../mainScene";
import { ROOT_SCALE } from "../rootScale";
import { RenderOrder } from "../RenderOrder";
import { localPlayerContainer } from "../../localplayer/LocalPlayerContainer";
import { POST_EFFECT_NUMBER_KEYS, normalizePostEffectNumber, RoomChangedMessage, RoomEnvironmentFacet, ChangeBackgroundMessage } from "flavr-common";
import { INFINITE_PLANE_MATERIAL } from "../shader/infinite.plane.material";
import { Module } from "../../module";
import { assertIsWeb } from "../../../messages/environment";
import { renderer } from "../RendererGlobals";
import { environmentSceneService } from "./EnvironmentSceneService";
import { GenerateSkyboxMessage, ResponseGenerateSkyboxMessage, SetGeneratedSkyboxMessage } from "../../../messages/rendering/SkyboxMessages";
import { modelPool } from "@modules/model";
import { preferencesService } from "../../preferences/PreferencesService";
import { MeshRenderable } from "../mesh/MeshRenderable";
assertIsWeb("BackgroundModule");
export function cloneUniforms(src) {
    const dst = {};
    for (const u in src) {
        dst[u] = {};
        for (const p in src[u]) {
            const property = src[u][p];
            if (property &&
                (property.isColor ||
                    property.isMatrix3 ||
                    property.isMatrix4 ||
                    property.isVector2 ||
                    property.isVector3 ||
                    property.isVector4 ||
                    property.isTexture ||
                    property.isQuaternion)) {
                dst[u][p] = property.clone();
            }
            else if (Array.isArray(property)) {
                dst[u][p] = property.slice();
            }
            else {
                dst[u][p] = property;
            }
        }
    }
    return dst;
}
const backgrounds = [
    {
        path: "img/WildSky/",
        files: [
            "WildSky_A_13_right.png",
            "WildSky_A_13_left.png",
            "WildSky_A_13_up.png",
            "WildSky_A_13_down.png",
            "WildSky_A_13_forward.png",
            "WildSky_A_13_back.png"
        ]
    },
    {
        path: "img/hills/",
        files: ["Hills_A_13_right.png", "Hills_A_13_left.png", "Hills_A_13_up.png", "Hills_A_13_down.png", "Hills_A_13_forward.png", "Hills_A_13_back.png"]
    },
    {
        path: "img/hills/",
        files: ["Hills_A_13_right.png", "Hills_A_13_left.png", "Hills_A_13_up.png", "gray.png", "Hills_A_13_forward.png", "Hills_A_13_back.png"]
    },
    {
        path: "img/plain_landscape/",
        files: ["right.jpg", "left.jpg", "up.jpg", "down.jpg", "front.jpg", "back.jpg"]
    },
    {
        path: "img/landscape4/",
        files: ["right.jpg", "left.jpg", "up.jpg", "down.jpg", "front.jpg", "back.jpg"]
    },
    {
        path: "img/surgery/",
        files: ["surgery_right.jpg", "surgery_left.jpg", "surgery_up.jpg", "surgery_down.jpg", "surgery_front.jpg", "surgery_back.jpg"]
    },
    {
        path: "img/hospital/",
        files: ["hospital_right.jpg", "hospital_left.jpg", "hospital_up.jpg", "hospital_down.jpg", "hospital_front.jpg", "hospital_back.jpg"]
    },
    {
        path: "img/machineshop/",
        files: ["machineshop_right.jpg", "machineshop_left.jpg", "machineshop_up.jpg", "machineshop_down.jpg", "machineshop_front.jpg", "machineshop_back.jpg"]
    },
    {
        path: "img/sunset/",
        files: ["sunset_right.jpg", "sunset_left.jpg", "sunset_up.jpg", "sunset_down.jpg", "sunset_front.jpg", "sunset_back.jpg"]
    }
];
const DEFAULT_BACKGROUND = 2;
const ENABLE_COORDINATE_SYSTEM = window.location.href.includes("coords");
const DEFAULT_ENVIRONMENT_PARAMS = {
    showSkybox: true,
    skyboxIndex: DEFAULT_BACKGROUND,
    useSkyboxAsEnvironmentMap: true,
    environmentMapRotation: 0,
    backgroundColor: 0x000000,
    showInfinitePlane: false,
    infinitePlaneColor: 0xa0a0a0,
    infinitePlaneElevation: 0.0
};
/**
 * Controls the room background, skybox and environment presentation so visual ambience can be changed independently from the rest of the scene graph.
 *
 * Facets:
 * - `RoomEnvironmentFacet` - Stores a room's environment parameters (skybox, background color, infinite plane settings); if the facet belongs to the room the client is currently in, the parameters are applied immediately.
 *
 * Messages:
 * - `ChangeBackgroundMessage` - Applies a new background configuration immediately.
 * - `GenerateSkyboxMessage` - Generates a cube map from a world position or model target; answers with a generated-map id when asked, or otherwise applies/stores it directly for the current or a specified room.
 * - `SetGeneratedSkyboxMessage` - Applies a previously generated skybox by its id.
 * - `RoomChangedMessage` - Reapplies the stored environment parameters, and any pending generated skybox, for the newly active room.
 */
export class BackgroundModule extends Module {
    /**
     * Sets up the default clear color and registers the runtime subscriptions (room environment facets, skybox
     * generation/application, background changes and room switches) that keep the scene background in sync.
     *
     * @param loadDefaultSkyboxAndModels - if false, `init` skips loading the default skybox/environment maps.
     */
    constructor(loadDefaultSkyboxAndModels = true) {
        var _a;
        super("BackgroundModule");
        this.loadDefaultSkyboxAndModels = loadDefaultSkyboxAndModels;
        this.skyMaterialArray = [];
        this.generatedEnviromentMaps = [];
        this.backgroundTextureCache = new Map();
        this.useSkyboxAsEnvironmentMap = DEFAULT_ENVIRONMENT_PARAMS.useSkyboxAsEnvironmentMap;
        this.showSkybox = DEFAULT_ENVIRONMENT_PARAMS.showSkybox;
        this.environmentRequestId = 0;
        this.pendingAsyncEnvironmentSwitch = false;
        this.roomEnvironmentParameters = new Map();
        this.roomInfos = new Map();
        this.handleChangeBackgroundMessage = (msg) => {
            this.update(msg.params, false);
        };
        this.handleGenerateSkyboxMessage = (msg, askId) => {
            const params = {
                near: 0.1,
                far: 1000,
                resolution: 512
            };
            const worldPosition = new Vector3();
            if (typeof msg.target === "number") {
                const model = modelPool.getModel(msg.target);
                if (model !== undefined) {
                    worldPosition.copy(model.getWorldPosition());
                    params.near = model.getWorldScale().length() + 0.1;
                }
                params.affectedUids = [msg.target];
            }
            else {
                worldPosition.fromArray(msg.target);
            }
            const envMap = environmentSceneService.generateCubeMap(worldPosition, params);
            if (askId !== undefined) {
                const id = this.generatedEnviromentMaps.length;
                this.generatedEnviromentMaps.push(envMap);
                this.connection.answer(askId, new ResponseGenerateSkyboxMessage(id));
                return;
            }
            if (msg.targetRoomUid !== undefined) {
                let roomInfo = this.roomInfos.get(msg.targetRoomUid);
                if (roomInfo === undefined) {
                    roomInfo = {};
                    this.roomInfos.set(msg.targetRoomUid, roomInfo);
                }
                roomInfo.skyMap = envMap;
            }
            if (msg.targetRoomUid === undefined || msg.targetRoomUid === this.currentRoomUid) {
                this.setGeneratedSkybox(envMap);
            }
        };
        this.handleSetGeneratedSkyboxMessage = (msg) => {
            const texture = this.generatedEnviromentMaps[msg.id];
            if (texture === undefined)
                return;
            this.setGeneratedSkybox(texture);
        };
        this.handleRoomChangedMessage = (msg) => {
            var _a;
            this.currentRoomUid = msg.uid;
            MeshRenderable.notifyRoomEntered(msg.uid);
            const params = (_a = this.roomEnvironmentParameters.get(msg.uid)) !== null && _a !== void 0 ? _a : {};
            this.update(params, true);
            this.updatePostEffectsPreference(params);
            const roomInfo = this.roomInfos.get(msg.uid);
            if ((roomInfo === null || roomInfo === void 0 ? void 0 : roomInfo.skyMap) !== undefined) {
                this.setGeneratedSkybox(roomInfo.skyMap);
            }
            if (!this.pendingAsyncEnvironmentSwitch) {
                MeshRenderable.notifyEnvironmentFinalized(msg.uid);
            }
        };
        this.connection.subscribeByFacetType(RoomEnvironmentFacet, (uid, facet) => {
            const model = modelPool.getModel(uid);
            if (model === undefined) {
                console.warn("RoomEnvironmentFacet with invalid uid", uid);
                return;
            }
            const roomModel = model.isRoom() ? model : model.getRoom();
            if (roomModel === undefined) {
                console.warn("RoomEnvironmentFacet with invalid roomId", model);
                return;
            }
            this.roomEnvironmentParameters.set(roomModel.getUID(), facet.params);
            if (this.currentRoomUid === roomModel.getUID()) {
                this.update(facet.params, true);
                this.updatePostEffectsPreference(facet.params);
            }
        });
        this.connection.subscribeByTypeOrAskRequest(GenerateSkyboxMessage, this.handleGenerateSkyboxMessage);
        this.connection.subscribeByType(SetGeneratedSkyboxMessage, this.handleSetGeneratedSkyboxMessage);
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(ChangeBackgroundMessage, this.handleChangeBackgroundMessage);
        renderer.setClearColor(new Color().setHex((_a = DEFAULT_ENVIRONMENT_PARAMS.backgroundColor) !== null && _a !== void 0 ? _a : 0x000000));
    }
    /**
     * Completes the background setup and registers the runtime callbacks that keep the active room background synchronized with incoming messages.
     */
    init() {
        this.createSkyboxMaterial();
        if (this.loadDefaultSkyboxAndModels) {
            this.initialBackgroundMap = this.loadBackground(backgrounds[DEFAULT_BACKGROUND]);
            this.initialEnvironmentMap = this.initialBackgroundMap.clone();
        }
        if (ENABLE_COORDINATE_SYSTEM) {
            this.createCoordinateSystem();
        }
        this.createInfinitePlane();
    }
    loadBackground(background) {
        this.loadedBackgroundKey = this.getBackgroundKey(background);
        textureLoaderService.setPath(background.path);
        const cached = this.backgroundTextureCache.get(this.loadedBackgroundKey);
        if (cached !== undefined) {
            this.ldrBackgroundMap = cached.ldrBackgroundMap;
            this.ldrEnvironmentMap = cached.ldrEnvironmentMap;
            console.log(`[env] reuse cached background textures: ${background.path}`);
        }
        else {
            this.ldrBackgroundMap = textureLoaderService.loadCube(background.files);
            this.ldrEnvironmentMap = textureLoaderService.loadCube(background.files);
            this.backgroundTextureCache.set(this.loadedBackgroundKey, {
                ldrBackgroundMap: this.ldrBackgroundMap,
                ldrEnvironmentMap: this.ldrEnvironmentMap
            });
        }
        this.activeBackgroundMap = this.ldrBackgroundMap;
        this.activeEnvironmentMap = this.ldrEnvironmentMap;
        this.setSceneBackground(this.ldrBackgroundMap);
        environmentSceneService.setDefaultBackground(this.ldrBackgroundMap);
        for (let i = 0; i < background.files.length; ++i) {
            const url = background.files[i];
            // Avoid rendering a transparent background while the skybox texture is loading
            textureLoaderService.loadTexture(url, (texture) => {
                const mat = this.skyMaterialArray[i];
                mat.map = texture;
                mat.needsUpdate = true;
            });
        }
        textureLoaderService.setPath("");
        this.applyEnvironmentMap(background);
        return this.ldrBackgroundMap;
    }
    getBackgroundKey(background) {
        return `${background.path}|${background.files.join("|")}`;
    }
    setSceneEnvironment(texture) {
        const oldValue = mainScene.environment;
        if (oldValue !== texture) {
            mainScene.environment = texture;
        }
        // During bootstrap the default LDR environment is set before a room is active.
        // Skip warmup here and let the first real room/environment trigger it.
        if (this.currentRoomUid === undefined) {
            if (texture !== null && oldValue !== texture) {
                console.log("[prewarm] skip bootstrap: no active room yet");
            }
            return;
        }
        // A null environment is a valid finalized state when the room disables skybox lighting.
        // Async environment changes notify again after they finish.
        if (!this.pendingAsyncEnvironmentSwitch) {
            MeshRenderable.notifyEnvironmentFinalized(this.currentRoomUid);
        }
    }
    setSceneBackground(texture) {
        mainScene.background = this.showSkybox ? texture : null;
    }
    updateHdrCache(key, hdrPmremTarget) {
        const previous = this.hdrTextureCache;
        this.hdrTextureCache = {
            key,
            hdrPmremTarget,
            texture: hdrPmremTarget.texture
        };
        // Only keep one HDR cache entry and free the previous one safely.
        if (previous !== undefined && previous.hdrPmremTarget !== hdrPmremTarget) {
            previous.hdrPmremTarget.dispose();
        }
    }
    applyEnvironmentMap(background) {
        var _a;
        const requestId = ++this.environmentRequestId;
        const fallbackEnvironment = this.ldrEnvironmentMap;
        if (fallbackEnvironment === undefined)
            return;
        const fallbackBackground = this.ldrBackgroundMap;
        const applyLdrEnvironment = () => {
            this.pendingAsyncEnvironmentSwitch = false;
            console.log(`[env] LDR fallback selected: path=${background.path}, requestId=${requestId}`);
            this.activeBackgroundMap = fallbackBackground;
            this.setSceneBackground(fallbackBackground !== null && fallbackBackground !== void 0 ? fallbackBackground : null);
            this.activeEnvironmentMap = fallbackEnvironment;
            this.setSceneEnvironment(this.useSkyboxAsEnvironmentMap ? fallbackEnvironment : null);
            environmentSceneService.setHdrEnvironmentActive(false);
            environmentSceneService.setDefaultEnvironment(fallbackEnvironment);
        };
        const hdrFile = HDR_ENVIRONMENT_FILES[background.path];
        if (hdrFile === undefined) {
            this.pendingAsyncEnvironmentSwitch = false;
            applyLdrEnvironment();
            return;
        }
        const hdrCacheKey = `${background.path}|${hdrFile}`;
        const cachedHdr = ((_a = this.hdrTextureCache) === null || _a === void 0 ? void 0 : _a.key) === hdrCacheKey ? this.hdrTextureCache : undefined;
        if (cachedHdr !== undefined) {
            this.pendingAsyncEnvironmentSwitch = false;
            console.log(`[env] reuse cached HDR environment: ${background.path}${hdrFile}`);
            this.activeBackgroundMap = cachedHdr.texture;
            this.setSceneBackground(cachedHdr.texture);
            this.hdrPmremTarget = cachedHdr.hdrPmremTarget;
            this.activeEnvironmentMap = cachedHdr.texture;
            this.setSceneEnvironment(this.useSkyboxAsEnvironmentMap ? cachedHdr.texture : null);
            environmentSceneService.setHdrEnvironmentActive(Boolean(this.useSkyboxAsEnvironmentMap && this.hdrPmremTarget !== undefined));
            environmentSceneService.setDefaultEnvironment(cachedHdr.texture);
            return;
        }
        this.pendingAsyncEnvironmentSwitch = true;
        console.log(`[env] HDR selected: path=${background.path}${hdrFile}, requestId=${requestId}`);
        textureLoaderService.setPath(background.path);
        textureLoaderService.loadHDR(hdrFile, (hdrTexture) => {
            if (requestId !== this.environmentRequestId) {
                hdrTexture.dispose();
                return;
            }
            const pmremGenerator = new PMREMGenerator(renderer);
            let hdrPmremTarget;
            try {
                hdrPmremTarget = pmremGenerator.fromEquirectangular(hdrTexture);
            }
            finally {
                pmremGenerator.dispose();
                hdrTexture.dispose();
            }
            if (requestId !== this.environmentRequestId) {
                hdrPmremTarget.dispose();
                return;
            }
            this.pendingAsyncEnvironmentSwitch = false;
            this.activeBackgroundMap = hdrPmremTarget.texture;
            this.setSceneBackground(hdrPmremTarget.texture);
            this.hdrPmremTarget = hdrPmremTarget;
            this.activeEnvironmentMap = hdrPmremTarget.texture;
            this.updateHdrCache(hdrCacheKey, hdrPmremTarget);
            this.setSceneEnvironment(this.useSkyboxAsEnvironmentMap ? hdrPmremTarget.texture : null);
            environmentSceneService.setHdrEnvironmentActive(Boolean(this.useSkyboxAsEnvironmentMap && this.hdrPmremTarget !== undefined));
            environmentSceneService.setDefaultEnvironment(hdrPmremTarget.texture);
        }, undefined, (error) => {
            if (requestId !== this.environmentRequestId)
                return;
            this.pendingAsyncEnvironmentSwitch = false;
            console.warn(`[renderer] Could not load HDR environment "${background.path}${hdrFile}". Using the LDR environment.`, error);
            applyLdrEnvironment();
        });
        textureLoaderService.setPath("");
    }
    update(params, useDefaults) {
        var _a, _b, _c, _d;
        const materialsNeedUpdate = false;
        let backgroundLoadTriggered = false;
        if (useDefaults) {
            params = Object.assign(Object.assign({}, DEFAULT_ENVIRONMENT_PARAMS), params);
        }
        if (params.showSkybox !== undefined)
            this.showSkybox = params.showSkybox;
        if (params.useSkyboxAsEnvironmentMap !== undefined)
            this.useSkyboxAsEnvironmentMap = params.useSkyboxAsEnvironmentMap;
        if (params.environmentMapRotation !== undefined) {
            const rotation = (-params.environmentMapRotation * Math.PI) / 180;
            mainScene.environmentRotation.set(0, rotation, 0);
            mainScene.backgroundRotation.set(0, rotation, 0);
        }
        renderer.setClearColor((_a = params.backgroundColor) !== null && _a !== void 0 ? _a : 0);
        if (params.skyboxIndex !== undefined && params.skyboxIndex < backgrounds.length) {
            const nextBackground = backgrounds[params.skyboxIndex];
            const nextBackgroundKey = this.getBackgroundKey(nextBackground);
            if (nextBackgroundKey !== this.loadedBackgroundKey) {
                backgroundLoadTriggered = true;
                this.loadBackground(nextBackground);
            }
            else {
                console.log(`[env] background unchanged, skip reload: ${nextBackground.path}`);
            }
        }
        if (params.showInfinitePlane !== undefined && this.infinitePlaneMesh !== undefined) {
            this.infinitePlaneMesh.visible = params.showInfinitePlane;
        }
        if (params.infinitePlaneColor !== undefined) {
            INFINITE_PLANE_MATERIAL.uniforms.uColor.value.setHex(params.infinitePlaneColor);
        }
        if (params.infinitePlaneElevation !== undefined) {
            INFINITE_PLANE_MATERIAL.uniforms.uPlainElevation.value = params.infinitePlaneElevation;
        }
        INFINITE_PLANE_MATERIAL.uniforms.uUseTexture.value = 0;
        if (materialsNeedUpdate) {
            mainScene.traverse((node) => {
                if (node instanceof Mesh) {
                    const mat = node.material;
                    if (Array.isArray(mat)) {
                        for (const m of mat) {
                            m.needsUpdate = true;
                        }
                    }
                    else if (mat) {
                        node.material.needsUpdate = true;
                    }
                }
            });
        }
        if (params.showSkybox !== undefined)
            this.setSceneBackground((_c = (_b = this.activeBackgroundMap) !== null && _b !== void 0 ? _b : this.ldrBackgroundMap) !== null && _c !== void 0 ? _c : null);
        if (params.useSkyboxAsEnvironmentMap !== undefined) {
            if (backgroundLoadTriggered) {
                console.log("[prewarm] wait env apply: background load in progress");
            }
            else {
                this.setSceneEnvironment(params.useSkyboxAsEnvironmentMap ? (_d = this.activeEnvironmentMap) !== null && _d !== void 0 ? _d : null : null);
            }
        }
        environmentSceneService.setHdrEnvironmentActive(Boolean(this.useSkyboxAsEnvironmentMap && this.hdrPmremTarget !== undefined));
        for (const it of this.skyMaterialArray) {
            it.color.setHSL(0, 0, 1);
        }
        const lightness = params.showSkybox ? 1 : 0;
        INFINITE_PLANE_MATERIAL.uniforms.uFogColor.value.setHSL(0, 0, lightness);
    }
    createSkyboxMaterial() {
        for (let i = 0; i < 6; i++) {
            const mat = new MeshBasicMaterial({
                side: BackSide,
                transparent: false
            });
            mat.depthTest = false;
            mat.depthWrite = false;
            this.skyMaterialArray.push(mat);
        }
    }
    createInfinitePlane() {
        const geometry = new SphereGeometry(1, 4, 1, undefined, undefined, 0.5 * Math.PI, 0.5 * Math.PI);
        this.infinitePlaneMesh = new Mesh(geometry, INFINITE_PLANE_MATERIAL);
        this.infinitePlaneMesh.renderOrder = RenderOrder.INFINITE_PLANE;
        this.infinitePlaneMesh.frustumCulled = false;
        this.infinitePlaneMesh.matrixAutoUpdate = false;
        this.infinitePlaneMesh.receiveShadow = true;
        this.infinitePlaneMesh.castShadow = false;
        this.infinitePlaneMesh.visible = false;
        localPlayerContainer.localPlayerRootNode.add(this.infinitePlaneMesh);
    }
    createCoordinateSystem() {
        const scale = 10 * ROOT_SCALE;
        const divisions = 10;
        const gridXZ = new GridHelper(scale, divisions, 0xff0000, 0x0000ff);
        const gridXY = new GridHelper(scale, divisions, 0xff0000, 0xffff00);
        const gridYZ = new GridHelper(scale, divisions, 0xff0000, 0x00ffff);
        gridXY.rotateX(Math.PI / 2);
        gridYZ.rotateZ(Math.PI / 2);
        mainScene.add(gridXY);
        mainScene.add(gridXZ);
        mainScene.add(gridYZ);
    }
    updatePostEffectsPreference(params) {
        var _a, _b, _c, _d;
        preferencesService.setMemoryPreference("showValuesInFlaVR", params.showValuesInFlaVR === true);
        preferencesService.onStoreChanged.invoke();
        preferencesService.updatePreferences([
            { key: "usePostEffects", value: (_a = params.usePostEffects) !== null && _a !== void 0 ? _a : false },
            { key: "useBloom", value: (_b = params.useBloom) !== null && _b !== void 0 ? _b : true },
            { key: "useGtao", value: (_c = params.useGtao) !== null && _c !== void 0 ? _c : true },
            { key: "gtaoScreenSpaceRadius", value: (_d = params.gtaoScreenSpaceRadius) !== null && _d !== void 0 ? _d : false },
            ...POST_EFFECT_NUMBER_KEYS.map((key) => ({ key, value: normalizePostEffectNumber(key, params[key]) }))
        ]);
    }
    setGeneratedSkybox(texture) {
        mainScene.background = texture;
        this.setSceneEnvironment(texture);
        environmentSceneService.setHdrEnvironmentActive(false);
    }
}
